import React, { useReducer, useRef, useEffect, useCallback, useState, cloneElement } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card, Typography, Space, message, Tooltip, Table, Tag, Button, Spin, Empty,
    Breadcrumb, Popconfirm, Tree, Dropdown,
} from 'antd';
import {
    CloudUploadOutlined, DownOutlined, FolderOpenOutlined, DeleteOutlined, DownloadOutlined,
    FileSearchOutlined, ReloadOutlined, InboxOutlined, LinkOutlined, HistoryOutlined,
    SyncOutlined, CloseCircleOutlined, SettingOutlined,
} from '@ant-design/icons';
import { useKeycloak } from '@react-keycloak/web';
import { useNotifications } from '../../contexts/NotificationContext';
import { useSearch } from '../../contexts/SearchContext';
import {
    getDataCollection, getCollectionFiles, uploadFileToCollection, deleteCollectionFile,
    downloadCollectionFile, previewCollectionFile, getCollectionEmbeddingStatus,
    addWebPageToCollection, refreshWebPage, getCollectionVersions, getPageIndexStructure,
} from '../../services/api';
import {
    getFileTypeIcon, formatFileSize, isDocumentFile, EmbeddingStatusTag, ALLOWED_EXTENSIONS,
    getDataCollectionsTablePagination,
} from './collectionUtils';
import { relativeTime, fullDateTime } from '../../utils/relativeTime';
import UploadModal from './UploadModal';
import PreviewModal from './PreviewModal';
import WebPageModal from './WebPageModal';
import VersionHistoryDrawer from './VersionHistoryDrawer';

const { Title, Text } = Typography;

const initialState = {
    collection: null, files: [], filesLoading: false,
    uploadModalVisible: false, uploadFiles: [], uploading: false,
    previewVisible: false, previewData: null, previewLoading: false,
    previewPage: 1, previewPageSize: 10, previewFileId: null,
    webpageModalVisible: false, webpageUrl: '', webpageMethod: 'GET', webpageJsonBody: '', addingWebpage: false,
    versionDrawerVisible: false, versions: [], versionsLoading: false,
    pageindexStructure: null, pageindexStructureLoading: false,
    selectedRowKeys: [], dragOver: false,
};

function reducer(state, action) {
    switch (action.type) {
        case 'SET': return { ...state, ...action.payload };
        case 'RESET_UPLOAD': return { ...state, uploadFiles: [], uploadModalVisible: false, uploading: false };
        default: return state;
    }
}

const CollectionDetail = ({ collectionId, onBack }) => {
    const navigate = useNavigate();
    const { keycloak } = useKeycloak();
    const { addNotification, updateNotification } = useNotifications();
    const { registerSearchHandler } = useSearch();
    const [s, dispatch] = useReducer(reducer, initialState);
    const set = (payload) => dispatch({ type: 'SET', payload });

    const fileInputRef = useRef(null);
    const [uploadMenuOpen, setUploadMenuOpen] = useState(false);

    const fetchDetail = useCallback(async () => {
        if (!keycloak?.authenticated) return;
        try { set({ collection: await getDataCollection(keycloak, collectionId) }); } catch (err) { console.error(err); }
    }, [keycloak, collectionId]);

    const fetchFiles = useCallback(async () => {
        if (!keycloak?.authenticated) return;
        set({ filesLoading: true });
        try { set({ files: (await getCollectionFiles(keycloak, collectionId)).map(f => ({ ...f, key: f.id.toString() })), filesLoading: false }); }
        catch (err) { message.error('Failed to load files'); set({ filesLoading: false }); }
    }, [keycloak, collectionId]);

    useEffect(() => { fetchDetail(); fetchFiles(); }, [fetchDetail, fetchFiles]);

    useEffect(() => {
        if (s.collection?.indexing_method === 'pageindex') {
            set({ pageindexStructureLoading: true });
            getPageIndexStructure(keycloak, collectionId)
                .then(d => set({ pageindexStructure: d })).catch(() => set({ pageindexStructure: null }))
                .finally(() => set({ pageindexStructureLoading: false }));
        } else { set({ pageindexStructure: null }); }
    }, [s.collection?.indexing_method, collectionId, keycloak]);

    const processDroppedOrSelectedFiles = useCallback((fileList) => {
        const validFiles = Array.from(fileList).filter(f => {
            const ext = f.name.split('.').pop()?.toLowerCase();
            return ALLOWED_EXTENSIONS.includes(ext);
        });
        if (validFiles.length !== fileList.length) message.warning('Some files were filtered out (unsupported type)');
        if (s.uploadModalVisible) {
            set({ uploadFiles: (() => { const existing = new Set(s.uploadFiles.map(f => f.name)); const nf = validFiles.filter(f => !existing.has(f.name)); if (nf.length < validFiles.length) message.warning('Some files skipped (already listed)'); return [...s.uploadFiles, ...nf]; })() });
        } else {
            set({ uploadFiles: validFiles });
            if (validFiles.length > 0) set({ uploadModalVisible: true });
        }
    }, [s.uploadModalVisible, s.uploadFiles]);

    const handleFileInputChange = (e) => { processDroppedOrSelectedFiles(e.target.files || []); if (fileInputRef.current) fileInputRef.current.value = ''; };
    const handleDrop = useCallback((e) => { e.preventDefault(); e.stopPropagation(); set({ dragOver: false }); if (e.dataTransfer.files?.length) processDroppedOrSelectedFiles(e.dataTransfer.files); }, [processDroppedOrSelectedFiles]);
    const handleDragOver = useCallback((e) => { e.preventDefault(); e.stopPropagation(); set({ dragOver: true }); }, []);
    const handleDragLeave = useCallback((e) => { e.preventDefault(); e.stopPropagation(); set({ dragOver: false }); }, []);

    const handleUploadFiles = async () => {
        if (s.uploadFiles.length === 0) return;
        set({ uploading: true, uploadModalVisible: false });
        try {
            for (const file of s.uploadFiles) {
                const isDoc = isDocumentFile(file.name);
                const notifId = await addNotification({ type: 'info', title: `Uploading ${file.name}`, message: 'Uploading file...', progress: 0, status: 'processing', dismissible: false });
                try {
                    await updateNotification(notifId, { progress: 20 });
                    const fd = new FormData(); fd.append('file', file, file.name);
                    if (isDoc) { fd.append('chunking_method', 'recursive'); fd.append('chunk_size', '512'); fd.append('chunk_overlap', '50'); }
                    const result = await uploadFileToCollection(keycloak, collectionId, fd);
                    if (isDoc && (result.file_id || result.id)) {
                        await updateNotification(notifId, { title: `Processing ${file.name}`, message: 'Upload complete, processing...', progress: 40 });
                        const fileId = result.file_id || result.id;
                        let pollAttempts = 0;
                        const pollInterval = setInterval(async () => {
                            try {
                                pollAttempts++;
                                const status = await getCollectionEmbeddingStatus(keycloak, collectionId);
                                const fs = status.files?.find(f => f.file_id === fileId || f.filename === file.name || f.original_filename === file.name);
                                const embStatus = fs?.embeddings_status || fs?.status || 'pending';
                                if (embStatus === 'completed') { clearInterval(pollInterval); await updateNotification(notifId, { title: `${file.name} processed`, message: 'Completed', progress: 100, status: 'completed', dismissible: true }); }
                                else if (embStatus === 'failed') { clearInterval(pollInterval); await updateNotification(notifId, { title: `${file.name} failed`, message: fs?.embeddings_error || 'Failed', status: 'error', dismissible: true }); }
                                else { await updateNotification(notifId, { message: embStatus === 'processing' ? 'Processing...' : 'Waiting...', progress: Math.min(40 + pollAttempts * 5, 95) }); }
                            } catch (err) { /* continue */ }
                        }, 2000);
                        setTimeout(() => { clearInterval(pollInterval); updateNotification(notifId, { title: `${file.name} uploaded`, message: 'Upload completed', progress: 100, status: 'completed', dismissible: true }); }, 300000);
                    } else {
                        await updateNotification(notifId, { title: `${file.name} uploaded`, message: 'Upload completed', progress: 100, status: 'completed', dismissible: true });
                    }
                } catch (err) { await updateNotification(notifId, { title: `${file.name} failed`, message: err.message || 'Upload failed', status: 'error', dismissible: true }); }
            }
            dispatch({ type: 'RESET_UPLOAD' }); fetchFiles(); fetchDetail();
        } catch (err) { message.error(err.message || 'Upload failed'); }
        finally { set({ uploading: false }); }
    };

    const handleDeleteFile = async (file) => { try { await deleteCollectionFile(keycloak, collectionId, file.id); message.success('File deleted'); fetchFiles(); fetchDetail(); } catch (err) { message.error(err.message || 'Failed'); } };
    const handleDownloadFile = async (file) => { try { await downloadCollectionFile(keycloak, collectionId, file.id, file.original_filename); } catch (err) { message.error('Failed to download'); } };
    const handlePreviewFile = useCallback(async (file) => {
        set({ previewFileId: file.id, previewPage: 1, previewPageSize: 10, previewVisible: true, previewLoading: true });
        try { set({ previewData: await previewCollectionFile(keycloak, collectionId, file.id, 0, 10), previewLoading: false }); }
        catch (err) { message.error('Failed to load preview'); set({ previewLoading: false }); }
    }, [keycloak, collectionId]);

    useEffect(() => registerSearchHandler(`${s.collection?.name || 'Collection'}`, (qRaw) => {
        const q = qRaw.trim().toLowerCase();
        if (!q) return [];
        return s.files
            .filter(f => f.original_filename?.toLowerCase().includes(q) || f.file_type?.toLowerCase().includes(q))
            .slice(0, 8)
            .map(f => ({ id: f.id, title: f.original_filename, subtitle: `${f.file_type?.toUpperCase()} · ${formatFileSize(f.file_size)}`, type: 'file', action: () => handlePreviewFile(f) }));
    }), [registerSearchHandler, s.files, s.collection?.name, handlePreviewFile]);
    const handlePreviewPageChange = async (page, pageSize) => { set({ previewPage: page, previewPageSize: pageSize }); if (s.previewFileId) { set({ previewLoading: true }); try { set({ previewData: await previewCollectionFile(keycloak, collectionId, s.previewFileId, (page - 1) * pageSize, pageSize), previewLoading: false }); } catch (err) { message.error('Preview failed'); set({ previewLoading: false }); } } };
    const handleRefreshStatus = async () => { try { await getCollectionEmbeddingStatus(keycloak, collectionId); fetchFiles(); fetchDetail(); } catch (err) { message.error('Failed to refresh'); } };
    const handleRefreshWebPage = async (file) => { try { await refreshWebPage(keycloak, collectionId, file.id, {}); message.success('Refreshed'); fetchFiles(); fetchDetail(); } catch (err) { message.error(err.message || 'Failed'); } };
    const resetWebpageModal = () => set({
        webpageModalVisible: false,
        webpageUrl: '',
        webpageMethod: 'GET',
        webpageJsonBody: '',
    });

    const handleAddWebPage = async () => {
        if (!s.webpageUrl.trim()) {
            message.error('Please enter a URL');
            return;
        }
        let jsonBody;
        if (s.webpageMethod === 'POST' && s.webpageJsonBody.trim()) {
            try {
                jsonBody = JSON.parse(s.webpageJsonBody);
            } catch {
                message.error('JSON body is not valid JSON');
                return;
            }
        }
        const payload = {
            url: s.webpageUrl.trim(),
            method: s.webpageMethod,
        };
        if (jsonBody !== undefined) payload.json_body = jsonBody;

        set({ addingWebpage: true });
        try {
            await addWebPageToCollection(keycloak, collectionId, payload);
            message.success('Web page added');
            const urlLabel = s.webpageUrl.trim();
            resetWebpageModal();
            addNotification({
                type: 'info',
                title: 'Processing web page',
                message: `Fetching (${s.webpageMethod}): ${urlLabel}`,
                status: 'processing',
                dismissible: false,
            });
            fetchFiles();
            fetchDetail();
        } catch (err) {
            message.error(err.message || 'Failed');
        } finally {
            set({ addingWebpage: false });
        }
    };
    const openVersionHistory = () => { set({ versionDrawerVisible: true, versionsLoading: true }); getCollectionVersions(keycloak, collectionId).then(d => set({ versions: d.versions || [] })).catch(() => {}).finally(() => set({ versionsLoading: false })); };

    const handleBulkDelete = async () => {
        for (const key of s.selectedRowKeys) { try { await deleteCollectionFile(keycloak, collectionId, Number(key)); } catch (err) { /* continue */ } }
        message.success(`Deleted ${s.selectedRowKeys.length} file(s)`); set({ selectedRowKeys: [] }); fetchFiles(); fetchDetail();
    };
    const handleBulkDownload = async () => {
        const files = s.selectedRowKeys
            .map(k => s.files.find(f => String(f.id) === String(k)))
            .filter(Boolean);
        if (files.length === 0) {
            message.warning('No files to download');
            return;
        }
        let failed = 0;
        for (let i = 0; i < files.length; i++) {
            try {
                await downloadCollectionFile(keycloak, collectionId, files[i].id, files[i].original_filename);
                if (i < files.length - 1) await new Promise(r => setTimeout(r, 450));
            } catch (err) { failed++; }
        }
        if (failed) message.error(`Could not download ${failed} file(s)`);
        else message.success(`Started download of ${files.length} file(s)`);
    };

    const fileColumns = [
        {
            title: 'Name', key: 'name', ellipsis: true,
            render: (_, record) => (
                <div className="file-name-cell">
                    {getFileTypeIcon(record.file_type, record.source_type)}
                    <div className="file-name-text">
                        <div className="file-name-label">{record.original_filename}</div>
                        <div className="file-desc-label">
                            {record.source_type === 'webpage' ? (
                                <Tooltip title={record.source_url}>
                                    <span>Web Page · </span>
                                    <a href={record.source_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12 }} onClick={e => e.stopPropagation()}>
                                        {record.source_url?.length > 50 ? record.source_url.slice(0, 50) + '…' : record.source_url}
                                    </a>
                                </Tooltip>
                            ) : (
                                <>{record.content_type === 'document' ? 'Document' : 'Tabular'} · {record.file_type?.toUpperCase()}</>
                            )}
                        </div>
                    </div>
                </div>
            ),
        },
        { title: 'Size', key: 'size', width: 90, render: (_, r) => formatFileSize(r.file_size) },
        {
            title: 'Chunks/Rows', key: 'rows', width: 100, align: 'center',
            render: (_, r) => { const c = r.row_count || 0; if (!c) return '-'; return `${c.toLocaleString()} ${r.content_type === 'document' ? 'chunks' : 'rows'}`; },
        },
        {
            title: s.collection?.indexing_method === 'pageindex' ? 'Index' : 'Embeddings', key: 'embeddings', width: 120,
            render: (_, r) => {
                const status = s.collection?.indexing_method === 'pageindex' ? (r.pageindex_status || r.embeddings_status) : r.embeddings_status;
                return (<Space size={4}><EmbeddingStatusTag status={status} />{status === 'failed' && r.embeddings_error && <Tooltip title={r.embeddings_error}><CloseCircleOutlined style={{ color: 'var(--color-error)', cursor: 'pointer' }} /></Tooltip>}</Space>);
            },
        },
        {
            title: 'Uploaded', dataIndex: 'created_at', key: 'created_at', width: 120,
            render: (date) => date ? <Tooltip title={fullDateTime(date)}><span style={{ whiteSpace: 'nowrap', fontSize: 13 }}>{relativeTime(date)}</span></Tooltip> : '-',
        },
        {
            title: '', key: 'actions', width: 140, className: 'file-actions-cell',
            render: (_, record) => (
                <Space size={2}>
                    {record.source_type === 'webpage' && <Tooltip title="Refresh"><Button type="text" icon={<SyncOutlined />} size="small" onClick={e => { e.stopPropagation(); handleRefreshWebPage(record); }} /></Tooltip>}
                    <Tooltip title="Preview"><Button type="text" icon={<FileSearchOutlined />} size="small" onClick={e => { e.stopPropagation(); handlePreviewFile(record); }} /></Tooltip>
                    <Tooltip title="Download"><Button type="text" icon={<DownloadOutlined />} size="small" onClick={e => { e.stopPropagation(); handleDownloadFile(record); }} /></Tooltip>
                    <Popconfirm title="Delete File" description={`Delete "${record.original_filename}"?`} onConfirm={e => { e?.stopPropagation(); handleDeleteFile(record); }} okText="Delete" okType="danger" cancelText="Cancel">
                        <Tooltip title="Delete"><Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={e => { e.preventDefault(); e.stopPropagation(); }} /></Tooltip>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const col = s.collection;
    const collectionModelLabel = col?.indexing_method === 'pageindex' ? (col?.pageindex_model || '') : (col?.embedding_model_name || '');
    return (
        <div className="collection-view" onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave}>
            <Breadcrumb className="collection-breadcrumb">
                <Breadcrumb.Item><a onClick={onBack}>Collections</a></Breadcrumb.Item>
                <Breadcrumb.Item>{col?.name || 'Loading...'}</Breadcrumb.Item>
            </Breadcrumb>

            <Card className="collection-header-card" bodyStyle={{ padding: '16px 20px' }}>
                <div className="detail-header-top">
                    <div className="detail-header-title-area">
                        <FolderOpenOutlined className="collection-icon" />
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <Title level={4} className="collection-title">{col?.name}</Title>
                            {col?.description && <Text type="secondary" className="collection-description">{col.description}</Text>}
                        </div>
                    </div>
                </div>

                <div className="detail-meta-bar">
                    <Space size={16} wrap>
                        <span className="detail-stat"><Text type="secondary">Files</Text> <Text strong>{col?.file_count || s.files.length}</Text></span>
                        <span className="detail-stat">
                            <Text type="secondary">Type</Text>
                            {col?.indexing_method === 'pageindex' ? (
                                <Tag color="geekblue" style={{ margin: 0 }}>PageIndex</Tag>
                            ) : col?.indexing_method === 'hybrid' ? (
                                <Tag color="green" style={{ margin: 0 }}>Hybrid</Tag>
                            ) : (
                                <Tag color="cyan" style={{ margin: 0 }}>Vector</Tag>
                            )}
                        </span>
                        <span className="detail-stat"><Text type="secondary">Status</Text> <EmbeddingStatusTag status={col?.embeddings_status || 'pending'} /></span>
                        {collectionModelLabel && (
                            <span className="detail-stat">
                                <Text type="secondary">Model</Text>
                                <Tag color="blue" style={{ margin: 0, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{collectionModelLabel}</Tag>
                            </span>
                        )}
                    </Space>
                    <div className="detail-actions">
                        <Tooltip title="Collection settings">
                            <Button
                                type="text"
                                size="small"
                                icon={<SettingOutlined />}
                                aria-label="Collection settings"
                                onClick={() => navigate(`/collections/${collectionId}/edit`)}
                            />
                        </Tooltip>
                        <Tooltip title="Refresh status"><Button type="text" icon={<ReloadOutlined />} size="small" onClick={handleRefreshStatus} /></Tooltip>
                        <Tooltip title="Version history"><Button type="text" icon={<HistoryOutlined />} size="small" onClick={openVersionHistory} /></Tooltip>
                        <Dropdown.Button
                            type="primary"
                            size="small"
                            icon={<DownOutlined />}
                            trigger={['click']}
                            open={uploadMenuOpen}
                            onOpenChange={setUploadMenuOpen}
                            onClick={() => { setUploadMenuOpen(false); fileInputRef.current?.click(); }}
                            buttonsRender={([leftBtn, rightBtn]) => [
                                cloneElement(leftBtn, { icon: <CloudUploadOutlined /> }),
                                rightBtn,
                            ]}
                            menu={{ items: [
                                { key: 'files', icon: <CloudUploadOutlined />, label: 'Upload files', onClick: () => { setUploadMenuOpen(false); fileInputRef.current?.click(); } },
                                { key: 'webpage', icon: <LinkOutlined />, label: 'Add web page', onClick: () => { setUploadMenuOpen(false); set({ webpageModalVisible: true }); } },
                            ] }}
                        >
                            Upload
                        </Dropdown.Button>
                    </div>
                </div>
            </Card>

            <input type="file" ref={fileInputRef} onChange={handleFileInputChange} style={{ display: 'none' }} multiple accept={ALLOWED_EXTENSIONS.map(ext => `.${ext}`).join(',')} />

            {s.dragOver && (
                <div className="drop-overlay">
                    <div className="drop-overlay-content">
                        <CloudUploadOutlined style={{ fontSize: 48, color: 'var(--color-primary)' }} />
                        <Title level={4} style={{ margin: '12px 0 0', color: 'var(--color-primary)' }}>Drop files here to upload</Title>
                    </div>
                </div>
            )}

            {col?.indexing_method === 'pageindex' && (
                <Card title={<Space><FileSearchOutlined /> Document Structure</Space>} style={{ marginBottom: 16 }}>
                    {s.pageindexStructureLoading ? (
                        <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>
                    ) : s.pageindexStructure?.structures?.length > 0 ? (
                        s.pageindexStructure.structures.map((doc, idx) => (
                            <div key={idx} style={{ marginBottom: idx < s.pageindexStructure.structures.length - 1 ? 16 : 0 }}>
                                <Text strong style={{ display: 'block', marginBottom: 8 }}>
                                    {doc.doc_name || `File ${doc.file_id}`}
                                    {doc.doc_description && <Text type="secondary" style={{ fontWeight: 'normal', marginLeft: 8 }}>{doc.doc_description}</Text>}
                                </Text>
                                <Tree
                                    showLine
                                    defaultExpandedKeys={doc.structure?.slice?.(0, 3)?.map?.((_, i) => `${doc.file_id}-${i}`) || []}
                                    treeData={(() => {
                                        const convert = (nodes, prefix) => (nodes || []).map((node, i) => {
                                            const key = `${prefix}-${i}`;
                                            const span = doc.type === 'pdf' ? `pp. ${node.start_index || '?'}-${node.end_index || '?'}` : `line ${node.line_num || '?'}`;
                                            return { key, title: (<span><strong>{node.title || 'Untitled'}</strong><Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>({span})</Text>{node.summary && <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>— {node.summary.length > 80 ? node.summary.slice(0, 80) + '...' : node.summary}</Text>}</span>), children: node.nodes ? convert(node.nodes, key) : undefined };
                                        });
                                        return convert(doc.structure, `${doc.file_id}`);
                                    })()}
                                />
                            </div>
                        ))
                    ) : (
                        <Empty description={col?.embeddings_status === 'processing' ? 'Indexing in progress...' : 'No indexed documents yet'} />
                    )}
                </Card>
            )}

            {s.selectedRowKeys.length > 0 && (
                <div className="bulk-action-bar">
                    <Space>
                        <Text strong>{s.selectedRowKeys.length} file{s.selectedRowKeys.length > 1 ? 's' : ''} selected</Text>
                        <Button size="small" icon={<DownloadOutlined />} onClick={handleBulkDownload}>Download</Button>
                        <Popconfirm title={`Delete ${s.selectedRowKeys.length} file(s)?`} onConfirm={handleBulkDelete} okText="Delete" okType="danger">
                            <Button danger size="small" icon={<DeleteOutlined />}>Delete</Button>
                        </Popconfirm>
                        <Button type="text" size="small" onClick={() => set({ selectedRowKeys: [] })}>Clear</Button>
                    </Space>
                </div>
            )}

            <div className="collection-files-section collections-table-animate">
                {s.files.length === 0 && !s.filesLoading ? (
                    <div className="collection-empty-state" style={s.dragOver ? { borderColor: 'var(--color-primary)', background: 'var(--color-primary-light)' } : {}}>
                        <InboxOutlined className="empty-icon" />
                        <Title level={5} type="secondary">No sources yet</Title>
                        <Text type="secondary">Upload files or add web pages to populate this collection</Text>
                        <div className="file-types-hint">
                            <span>Supports: </span>
                            {['PDF', 'DOCX', 'TXT', 'MD', 'CSV', 'XLSX'].map(t => <Tag key={t} className="file-type-tag">{t}</Tag>)}
                            <Tag className="file-type-tag" style={{ borderColor: 'var(--color-primary)' }}><LinkOutlined /> Web Pages</Tag>
                        </div>
                    </div>
                ) : (
                    <Table
                        columns={fileColumns}
                        dataSource={s.files}
                        rowKey={(record) => String(record.id)}
                        loading={s.filesLoading}
                        pagination={getDataCollectionsTablePagination(20, { totalItemLabel: 'files' })}
                        size="middle"
                        scroll={{ x: 'max-content' }}
                        rowSelection={{ selectedRowKeys: s.selectedRowKeys, onChange: keys => set({ selectedRowKeys: keys }) }}
                        onRow={(record) => ({ onClick: () => handlePreviewFile(record), style: { cursor: 'pointer' } })}
                    />
                )}
            </div>

            <UploadModal open={s.uploadModalVisible} files={s.uploadFiles} uploading={s.uploading} onCancel={() => dispatch({ type: 'RESET_UPLOAD' })} onUpload={handleUploadFiles} onRemoveFile={idx => set({ uploadFiles: s.uploadFiles.filter((_, i) => i !== idx) })} onAddMore={() => fileInputRef.current?.click()} />
            <PreviewModal open={s.previewVisible} data={s.previewData} loading={s.previewLoading} page={s.previewPage} pageSize={s.previewPageSize} onPageChange={handlePreviewPageChange} onClose={() => set({ previewVisible: false })} />
            <WebPageModal
                open={s.webpageModalVisible}
                url={s.webpageUrl}
                method={s.webpageMethod}
                jsonBody={s.webpageJsonBody}
                loading={s.addingWebpage}
                onUrlChange={v => set({ webpageUrl: v })}
                onMethodChange={v => set({ webpageMethod: v })}
                onJsonBodyChange={v => set({ webpageJsonBody: v })}
                onCancel={resetWebpageModal}
                onSubmit={handleAddWebPage}
            />
            <VersionHistoryDrawer open={s.versionDrawerVisible} versions={s.versions} loading={s.versionsLoading} onClose={() => set({ versionDrawerVisible: false })} />
        </div>
    );
};

export default CollectionDetail;
